import React, { useEffect, useState } from 'react';
import {
  Layout,
  Typography,
  Button,
  Collapse,
  Spin,
  Empty,
  FloatButton,
  message,
  Grid,
  Drawer,
  Menu,
} from 'antd';
import { PlusOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { DataProvider, useDataContext } from './contexts/DataContext';
import { Soup, Exchange, Filter } from './types/api.types';
import FiatCard from './components/FiatCard';
import AddFiatModal from './components/AddFiatModal';
import AddSoupModal from './components/AddSoupModal';
import EditSoupModal from './components/EditSoupModal';
import EditExchangeModal from './components/EditExchangeModal';
import EditFilterModal from './components/EditFilterModal';
import ConfirmDeleteModal from './components/ConfirmDeleteModal';
import UpdateBanksModal from './components/UpdateBanksModal';
import AddFiatsSettingsModal from './components/AddFiatsSettings';
import LoginModal from './components/LoginModal';
import ApiService from './services/api';
import { MESSAGES } from './utils/constants';
import 'antd/dist/reset.css';

const { Header, Content } = Layout;
const { Title } = Typography;
const { useBreakpoint } = Grid;

const AppContent: React.FC = () => {
  const {
    usedFiats,
    fiatSettings,
    loading,
    editingItem,
    refreshData,
    setEditingItem,
    loadFiatSettings,
    removeFiatFromCache,
    isAuthenticated,
  } = useDataContext();

  const [addFiatModalVisible, setAddFiatModalVisible] = useState(false);
  const [addSoupModalVisible, setAddSoupModalVisible] = useState(false);
  const [editSoupModalVisible, setEditSoupModalVisible] = useState(false);
  const [editExchangeModalVisible, setEditExchangeModalVisible] = useState(false);
  const [editFilterModalVisible, setEditFilterModalVisible] = useState(false);
  const [settingsDrawerVisible, setSettingsDrawerVisible] = useState(false);
  const [updateBanksModalVisible, setUpdateBanksModalVisible] = useState(false);
  const [addFiatsSettingsVisible, setAddFiatsSettingsVisible] = useState(false);
  const [currentEditData, setCurrentEditData] = useState<{
    soup?: Soup;
    exchange?: Exchange;
    filter?: Filter;
    filterType?: 'BUY' | 'SELL';
    fiatName?: string;
    addSoupFiatName?: string;
  }>({});

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  if (!isAuthenticated) {
    return (
      <Layout style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <LoginModal />
      </Layout>
    );
  }

  // Обработчики для редактирования
  const handleEditSoup = (soup: Soup, fiatName: string) => {
    if (editingItem) {
      message.warning('Завершите текущее редактирование');
      return;
    }
    setEditingItem({ type: 'soup', id: soup.id });
    setCurrentEditData({ soup, fiatName });
    setEditSoupModalVisible(true);
  };

  const handleEditExchange = (exchange: Exchange, fiatName: string) => {
    if (editingItem) {
      message.warning('Завершите текущее редактирование');
      return;
    }
    setEditingItem({ type: 'exchange', id: exchange.id });
    setCurrentEditData({ exchange, fiatName });
    setEditExchangeModalVisible(true);
  };

  const handleEditFilter = (filter: Filter, filterType: 'BUY' | 'SELL', fiatName: string) => {
    if (editingItem) {
      message.warning('Завершите текущее редактирование');
      return;
    }
    setEditingItem({ type: 'filter', id: filter.id });
    setCurrentEditData({ filter, filterType, fiatName });
    setEditFilterModalVisible(true);
  };

  // Обработчики для удаления
  const handleDeleteFiat = (fiatName: string) => {
    if (editingItem) {
      message.warning('Завершите текущее редактирование');
      return;
    }

    ConfirmDeleteModal({
      type: 'fiat',
      itemName: fiatName,
      onConfirm: async () => {
        try {
          await ApiService.deleteFiat(fiatName);
          message.success(MESSAGES.SUCCESS.FIAT_DELETED);
          removeFiatFromCache(fiatName);
        } catch (error) {
          console.error('Ошибка удаления валюты:', error);
          message.error(MESSAGES.ERROR.GENERIC);
        }
      },
      onCancel: () => {},
    });
  };

  const handleDeleteSoup = (soupId: number, fiatName: string) => {
    if (editingItem) {
      message.warning('Завершите текущее редактирование');
      return;
    }

    ConfirmDeleteModal({
      type: 'soup',
      onConfirm: async () => {
        try {
          await ApiService.deleteSoup(soupId);
          message.success(MESSAGES.SUCCESS.SOUP_DELETED);
          await loadFiatSettings(fiatName);
        } catch (error) {
          console.error('Ошибка удаления супа:', error);
          message.error(MESSAGES.ERROR.GENERIC);
        }
      },
      onCancel: () => {},
    });
  };

  const handleDeleteExchange = (exchangeId: number, fiatName: string) => {
    if (editingItem) {
      message.warning('Завершите текущее редактирование');
      return;
    }

    ConfirmDeleteModal({
      type: 'exchange',
      onConfirm: async () => {
        try {
          await ApiService.deleteExchange(exchangeId);
          message.success(MESSAGES.SUCCESS.EXCHANGE_DELETED);
          await loadFiatSettings(fiatName);
        } catch (error) {
          console.error('Ошибка удаления биржи:', error);
          message.error(MESSAGES.ERROR.GENERIC);
        }
      },
      onCancel: () => {},
    });
  };

  // Обработчик для добавления супа
  const handleAddSoup = (fiatName: string) => {
    if (editingItem) {
      message.warning('Завершите текущее редактирование');
      return;
    }
    setCurrentEditData({ addSoupFiatName: fiatName });
    setAddSoupModalVisible(true);
  };

  // Обработчики для закрытия модальных окон
  const handleCloseEditModals = () => {
    setEditingItem(null);
    setCurrentEditData({});
    setEditSoupModalVisible(false);
    setEditExchangeModalVisible(false);
    setEditFilterModalVisible(false);
    setAddSoupModalVisible(false);
  };

  const handleSuccessEdit = () => {
    handleCloseEditModals();
  };

  const handleAddFiatSuccess = () => {
    setAddFiatModalVisible(false);
  };

  const handleAddSoupSuccess = () => {
    setAddSoupModalVisible(false);
    setCurrentEditData({});
  };

  // Генерация элементов коллапса
  const generateCollapseItems = () => {
    // Защита от случаев, когда usedFiats не является массивом
    if (!Array.isArray(usedFiats)) {
      console.warn('usedFiats не является массивом:', usedFiats);
      return [];
    }

    console.log('🔧 Генерация элементов коллапса для валют:', usedFiats);
    console.log('📦 Доступные настройки fiatSettings:', Object.keys(fiatSettings));

    return usedFiats.map((fiatName) => {
      const settings = fiatSettings[fiatName];
      console.log(`🔍 Обработка валюты ${fiatName}:`, {
        hasSettings: !!settings,
        exchange_soups: settings?.exchange_soups,
        exchange_soups_count: settings?.exchange_soups?.length || 0
      });

      if (!settings) {
        console.warn(`❌ Настройки для ${fiatName} не найдены`);
        return null;
      }

      if (!settings.exchange_soups || settings.exchange_soups.length === 0) {
        console.warn(`⚠️ У валюты ${fiatName} нет exchange_soups`);
        return null;
      }

      return {
        key: fiatName,
        label: (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            width: '100%'
          }}>
            <span style={{ fontSize: '16px', fontWeight: 500 }}>
              {fiatName}
            </span>
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteFiat(fiatName);
              }}
              disabled={!!editingItem}
            />
          </div>
        ),
        children: (
          <div>
            {settings.exchange_soups.map((soup) => (
              <FiatCard
                key={soup.id}
                soup={soup}
                fiatName={fiatName}
                totalSoupsCount={settings.exchange_soups.length}
                onEditSoup={(soup) => handleEditSoup(soup, fiatName)}
                onDeleteSoup={(soupId) => handleDeleteSoup(soupId, fiatName)}
                onEditExchange={(exchange) => handleEditExchange(exchange, fiatName)}
                onDeleteExchange={(exchangeId) => handleDeleteExchange(exchangeId, fiatName)}
                onEditFilter={(filter, filterType) => handleEditFilter(filter, filterType, fiatName)}
              />
            ))}
            {/* Кнопка добавления нового супа */}
            <div style={{ marginTop: 16 }}>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => handleAddSoup(fiatName)}
                disabled={!!editingItem}
                style={{ width: '100%' }}
              >
                Добавить новый суп
              </Button>
            </div>
          </div>
        ),
      };
    }).filter((item): item is NonNullable<typeof item> => item !== null);
  };

  const collapseItems = generateCollapseItems();

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Header style={{ 
        backgroundColor: '#fff', 
        boxShadow: '0 1px 4px rgba(0,21,41,.08)',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Button 
            type="text"
            icon={<SettingOutlined style={{ fontSize: '22px' }} />} 
            onClick={() => setSettingsDrawerVisible(true)}
            disabled={!!editingItem}
            style={{ 
              width: 44, 
              height: 44, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              lineHeight: 0 
            }}
          />
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            Админская панель криптообменника
          </Title>
        </div>
        {!isMobile && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setAddFiatModalVisible(true)}
            disabled={!!editingItem}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
              lineHeight: 0 }}
          >
            Добавить валюту
          </Button>
        )}
      </Header>

      <Content style={{ padding: '24px', maxWidth: 1200, margin: '0 auto', width: '100%' }}>
        <Spin spinning={loading} tip="Загрузка данных...">
          {(!Array.isArray(usedFiats) || usedFiats.length === 0) && !loading ? (
            <Empty
              description="Валюты не найдены"
              style={{ marginTop: 100 }}
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setAddFiatModalVisible(true)}
                disabled={!!editingItem}
              >
                Добавить первую валюту
              </Button>
            </Empty>
          ) : (
            <Collapse
              accordion
              size="large"
              items={collapseItems}
              style={{ backgroundColor: '#fff' }}
            />
          )}
        </Spin>

        {/* Floating Action Button для мобильных устройств */}
        {isMobile && !editingItem && (
          <FloatButton
            icon={<PlusOutlined />}
            type="primary"
            onClick={() => setAddFiatModalVisible(true)}
          />
        )}

        {/* Модальные окна */}
        <AddFiatModal
          visible={addFiatModalVisible}
          onCancel={() => setAddFiatModalVisible(false)}
          onSuccess={handleAddFiatSuccess}
        />

        <AddSoupModal
          visible={addSoupModalVisible}
          fiatName={currentEditData.addSoupFiatName || ''}
          onCancel={() => setAddSoupModalVisible(false)}
          onSuccess={handleAddSoupSuccess}
        />

        <EditSoupModal
          visible={editSoupModalVisible}
          soup={currentEditData.soup || null}
          fiatName={currentEditData.fiatName || ''}
          onCancel={handleCloseEditModals}
          onSuccess={handleSuccessEdit}
        />

        <EditExchangeModal
          visible={editExchangeModalVisible}
          exchange={currentEditData.exchange || null}
          fiatName={currentEditData.fiatName || ''}
          onCancel={handleCloseEditModals}
          onSuccess={handleSuccessEdit}
        />

        <EditFilterModal
          visible={editFilterModalVisible}
          filter={currentEditData.filter || null}
          filterType={currentEditData.filterType}
          fiatName={currentEditData.fiatName || ''}
          onCancel={handleCloseEditModals}
          onSuccess={handleSuccessEdit}
        />

        <Drawer
          title="Настройки"
          placement="left"
          onClose={() => setSettingsDrawerVisible(false)}
          open={settingsDrawerVisible}
        >
           <Menu
            mode="inline"
            style={{ borderRight: 0 }}
            items={[
              {
                key: 'update_banks',
                label: 'Обновление банков',
                onClick: () => {
                   setSettingsDrawerVisible(false);
                   setUpdateBanksModalVisible(true);
                }
              },
              {
                key: 'add_fiats',
                label: 'Добавление валют',
                onClick: () => {
                   setSettingsDrawerVisible(false);
                   setAddFiatsSettingsVisible(true);
                }
              }
            ]}
          />
        </Drawer>

        <UpdateBanksModal
           visible={updateBanksModalVisible}
           onCancel={() => setUpdateBanksModalVisible(false)}
           onSuccess={() => {
              message.success('Данные обновлены');
              refreshData();
           }}
        />

        <AddFiatsSettingsModal
           visible={addFiatsSettingsVisible}
           onCancel={() => setAddFiatsSettingsVisible(false)}
           onSuccess={() => {
              // message is handled in the modal
              refreshData();
              setAddFiatsSettingsVisible(false);
           }}
        />
      </Content>
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <DataProvider>
      <AppContent />
    </DataProvider>
  );
};

export default App;
